import { parse } from 'valibot'
import { whitelistedFields } from './common'

export const termsRegEx = /^\{!terms f=([a-zA-Z_]\w*) v=([^}]+)\}$/
export const qfPartRegex = /^([a-z_]\w*)(?:\^(\d+(?:\.\d+)?))?$/i
export const rowPrefixRegEx = /^\$row\.[a-zA-Z_]\w*$/

// Regex for single condition in filter query, allowing optional neglation
const singleFilterQueryRegEx = /-?[a-zA-Z_]\w*:(?:"[^"\n]*"|\[[^\s:[\]]+ TO [^\s:[\]]+\]|[^\s:[\]"]+)/
// regex for filter queries, allowing multiple conditions with AND/OR
export const filterQueryRegEx = new RegExp(`^(${singleFilterQueryRegEx.source})(?:\\s+(AND|OR)\\s+(${singleFilterQueryRegEx.source}))*$`)

export const baseSuspiciousPatterns = [
  // eslint-disable-next-line regexp/optimal-lookaround-quantifier
  /\{!(?!terms\s+f=[a-zA-Z_]+\s+v=[^}]+)[^}]*\}/, // Allow {!terms f=<field> v=<value>}, flag other local params
  /\*:\*/, // Match all queries
  /\b(admin|password|secret|token)\b/i, // Sensitive field names
  /~\d*\.?\d*/, // Fuzzy search manipulation
  /\{!func\}/i, // Query functions
  /\{!join\}/i, // Join queries
  /\{!collapse\}/i, // Collapse queries
  /script:/i, // Script-like patterns
  /[a-z]+\(/i, // Broader function-like patterns
]

const operatorPatterns = [/\b(AND|OR)\b/i]

const suspiciousPatterns = [
  ...baseSuspiciousPatterns,
  ...operatorPatterns,
]

/**
 * Checks for suspicious patterns in input, logging detected patterns.
 */
export function containsSuspiciousPatterns(input: string, patterns: RegExp[] = suspiciousPatterns): boolean {
  return patterns.some(pattern => pattern.test(input))
}

/**
 * Appends ' AND' to the query term if it is not empty.
 */
export function appendAndCondition(fqTerm: string): string {
  if (typeof fqTerm !== 'string') {
    throw createError({
      statusCode: 400,
      statusMessage: 'Invalid query term type',
    })
  }

  return fqTerm.trim().length > 0 ? ' AND ' : ''
}

/**
 * Sanitizes input value with configurable options.
 */
export function sanitizeInput(value: string, options: { allowWildcards?: boolean, allowSpaces?: boolean, allowNorwegianLetters?: boolean, allowPipe?: boolean, maxLength?: number, throwOnNonString?: boolean } = {}): string {
  const { allowWildcards = false, allowSpaces = false, allowNorwegianLetters = true, allowPipe = false, maxLength = 100, throwOnNonString = true } = options

  if (typeof value !== 'string') {
    if (throwOnNonString) {
      throw createError({
        statusCode: 400,
        statusMessage: 'Invalid value type',
      })
    }
    return ''
  }

  if (value.length > maxLength) {
    throw createError({
      statusCode: 400,
      statusMessage: 'Input too long',
    })
  }

  let allowedChars = 'a-zA-Z0-9_'
  if (allowNorwegianLetters) {
    allowedChars += 'æøåÆØÅ'
  }
  // Always allow forward slashes as they're commonly used in URLs, file paths, and hierarchical identifiers
  allowedChars += '\\-\\/\\.'
  if (allowWildcards) {
    allowedChars += '\\?\\*'
  }
  if (allowSpaces) {
    allowedChars += ' '
  }
  if (allowPipe) {
    allowedChars += '\\|'
  }

  const regex = new RegExp(`[^${allowedChars}]`, 'g')

  const sanitized = value
    .replace(regex, '')
    .replace(/\s+/g, allowSpaces ? ' ' : '') // Collapse multiple spaces or remove them
    .trim()
    .substring(0, maxLength)

  if (!sanitized) {
    throw createError({
      statusCode: 400,
      statusMessage: 'Invalid value',
    })
  }

  // TODO: Send error message to datadog when sanized value is not the same as the input value

  return sanitized
}

/**
 * Sanitizes value to prevent injection attacks, with options for wildcards and spaces.
 */
export function sanitizeValue(value: string, options: { allowWildcards?: boolean, allowSpaces?: boolean } = {}): string {
  return sanitizeInput(value, { ...options, allowNorwegianLetters: true, throwOnNonString: true })
}

/**
 * Sanitizes ID values to allow wildcards (*, ?) and Norwegian letters.
 */
export function sanitizeIdValue(value: string): string {
  return sanitizeInput(value, { allowWildcards: true, allowSpaces: false, allowNorwegianLetters: true, maxLength: 1000 })
}

/**
 * Escapes Solr wildcard operators so '*' and '?' are treated as literal characters.
 */
export function escapeSolrLiteralWildcards(value: string): string {
  return value.replaceAll('*', '\\*').replaceAll('?', '\\?')
}

/**
 * Sanitizes and validates array inputs.
 */
export function sanitizeQueryValue(values: unknown | string | string[], maxLength: number = 50): string[] {
  const sanitizeOptions = { allowWildcards: false, allowSpaces: true, allowNorwegianLetters: true, throwOnNonString: false }

  if (!Array.isArray(values)) {
    const sanitized = sanitizeInput(String(values), sanitizeOptions)
    return [sanitized]
  }

  if (values.length > maxLength) {
    throw createError({
      statusCode: 400,
      statusMessage: 'Too many array elements',
    })
  }

  const sanitized = values
    .map(value => sanitizeInput(String(value), sanitizeOptions))
    .filter(value => value && !containsSuspiciousPatterns(value))

  if (sanitized.length === 0) {
    throw createError({
      statusCode: 400,
      statusMessage: 'Invalid array input',
    })
  }

  return sanitized
}

/**
 * Creates a quoted field value for Solr queries.
 */
export function createQuotedFieldValue(value: string, options: { allowPipe?: boolean, escape?: boolean } = {}): string {
  if (typeof value !== 'string') {
    return ''
  }

  if (options.escape) {
    return `"${value.replace(/"/g, '\\"')}"`
  }

  const sanitized = sanitizeInput(value, { allowSpaces: true, allowNorwegianLetters: true, allowPipe: options.allowPipe, maxLength: 200 })
  return `"${sanitized}"`
}

/**
 * Parses terms queries with validation.
 */
export function parseTermsQuery(field: string, value: string): string {
  const sanitizeOptions = { allowWildcards: false, allowSpaces: true, allowNorwegianLetters: true, throwOnNonString: false }

  try {
    const parsedField = parse(whitelistedFields, field)

    if (typeof value !== 'string' || !value.trim()) {
      throw new Error('Invalid value for terms query')
    }

    let sanitizedValue = value
    if (value.startsWith('$row.')) {
      if (!rowPrefixRegEx.test(value)) {
        throw new Error('Invalid $row. prefix format')
      }
    }
    else {
      sanitizedValue = sanitizeInput(value, sanitizeOptions)
    }

    if (containsSuspiciousPatterns(sanitizedValue)) {
      throw new Error('Suspicious pattern detected in terms query value')
    }

    const result = `{!terms f=${parsedField} v=${sanitizedValue}}`
    return result
  }
  catch (error) {
    throw createError({
      statusCode: 400,
      statusMessage: `Invalid field format: ${error instanceof Error ? error.message : 'Unknown error'}`,
    })
  }
}

/**
 * Creates a safe field-value pair for Solr queries.
 */
export function safeFieldValue(field: string, value?: string | number, options: { quote?: boolean, sanitize?: boolean, allowWildcards?: boolean, allowSpaces?: boolean, allowNorwegianLetters?: boolean, allowPipe?: boolean } = {}): string {
  const { quote = false, sanitize = true, allowWildcards = false, allowSpaces = field !== 'id', allowNorwegianLetters = true, allowPipe = field === 'author_list' } = options
  try {
    const parsedField = parse(whitelistedFields, field)

    if (value === undefined) {
      return ''
    }

    // Coerce to string if it's a number (common for config values like path locations)
    const stringValue = typeof value === 'number' ? value.toString() : value

    const finalValue = sanitize ? sanitizeInput(stringValue, { allowWildcards, allowSpaces, allowNorwegianLetters, allowPipe }) : stringValue

    const result = `${parsedField}:${quote ? createQuotedFieldValue(finalValue, { allowPipe }) : finalValue}`
    return result
  }
  catch {
    throw createError({
      statusCode: 400,
      statusMessage: 'Invalid query parameters',
    })
  }
}

/** Split fl string on commas, but keep bracketed expressions (e.g. [child ...]) intact. */
function splitFlFields(fl: string): string[] {
  const fields: string[] = []
  let current = ''
  let depth = 0

  for (const char of fl) {
    if (char === '[') {
      depth++
    }
    else if (char === ']') {
      depth--
    }

    if (char === ',' && depth === 0) {
      fields.push(current.trim())
      current = ''
    }
    else {
      current += char
    }
  }

  if (current.trim()) {
    fields.push(current.trim())
  }

  return fields
}

/**
 * Validates and sanitizes the fl parameter for Solr queries.
 */
export function validateFlParameter(fl: string): string {
  if (typeof fl !== 'string' || !fl.trim()) {
    throw createError({
      statusCode: 400,
      statusMessage: 'Invalid fl parameter',
    })
  }

  const trimmedFl = fl.trim()
  const fields = splitFlFields(trimmedFl)

  const validatedFields = fields.map((field) => {
    if (field === '*' || field.match(/^\[child limit=\d+\]$/) || field.match(/^\w+:\[subquery\]$/)) {
      return field
    }

    // Match [child fl=field1,field2 limit=N] — validate inner fields against whitelist
    const childFlMatch = field.match(/^\[child fl=(.+) limit=(\d+)\]$/)
    if (childFlMatch?.[1] && childFlMatch[2]) {
      const innerFields = childFlMatch[1].split(',').map(f => f.trim())
      for (const innerField of innerFields) {
        try {
          parse(whitelistedFields, innerField)
        }
        catch (error) {
          throw createError({
            statusCode: 400,
            statusMessage: `Invalid field in [child fl=...]: ${error instanceof Error ? error.message : 'Unknown error'}`,
          })
        }
      }
      return `[child fl=${innerFields.join(',')} limit=${childFlMatch[2]}]`
    }

    try {
      return parse(whitelistedFields, field)
    }
    catch (error) {
      throw createError({
        statusCode: 400,
        statusMessage: `Invalid fl format: ${error instanceof Error ? error.message : 'Unknown error'}`,
      })
    }
  })

  const result = validatedFields.join(',')
  if (containsSuspiciousPatterns(result)) {
    throw createError({
      statusCode: 400,
      statusMessage: 'Suspicious patterns detected in fl parameter',
    })
  }

  return result
}

/**
 * Validates and sanitizes the qf parameter for Solr queries.
 */
export function validateQfParameter(qf: string): string {
  if (typeof qf !== 'string' || !qf.trim()) {
    throw createError({
      statusCode: 400,
      statusMessage: 'Invalid qf parameter',
    })
  }

  const parts = qf.trim().split(/\s+/)

  const validatedParts = parts.map((part) => {
    const match = qfPartRegex.exec(part)
    if (!match) {
      throw createError({
        statusCode: 400,
        statusMessage: `Invalid qf part: ${part}`,
      })
    }
    const fieldName = match[1]
    const boost = match[2] ? `^${match[2]}` : ''
    try {
      parse(whitelistedFields, fieldName)
    }
    catch {
      throw createError({
        statusCode: 400,
        statusMessage: `Invalid field name in qf: ${fieldName}`,
      })
    }
    return `${fieldName}${boost}`
  })

  const result = validatedParts.join(' ')
  if (containsSuspiciousPatterns(result)) {
    throw createError({
      statusCode: 400,
      statusMessage: 'Suspicious patterns detected in qf parameter',
    })
  }

  return result
}

/**
 * Formats a field-boost mapping into Solr's field^boost syntax.
 * Used for qf, pf, pf2, pf3, and any other boost parameters.
 *
 * @example formatFieldBoosts({ title: 60, bodytext: 10 }) => "title^60 bodytext^10"
 */
export function formatFieldBoosts(fields: Record<string, number>): string {
  return Object.entries(fields)
    .map(([field, boost]) => `${field}^${boost}`)
    .join(' ')
}
